import { useFetcher } from "@remix-run/react";
import { ShirtspaceResponse } from "@services/shirtspace.types";
import React, { useEffect, useRef, useState } from "react";
import SuccessModal from "~/components/ProductAddToCart/SuccessModal";
import { useEasySize } from "~/components/ProductAddToCart/useEasySize";
import { AddToCartActionResponse } from "~/entities/actions/types";
import { WindowWithGTM } from "~/entities/global/types";
import { Product, ProductAvailability } from "~/entities/product/types";
import { User } from "~/entities/user/types";
import { SelectedVariant } from "~/entities/variant/types";

type Props = {
  product: Product;
  selectedVariant: SelectedVariant;
  children: React.ReactNode;
  productAvailability: Promise<ShirtspaceResponse<ProductAvailability>>;
  user: User | null;
};

const ProductAddToCart: React.FC<Props> = ({
  children,
  product,
  selectedVariant,
  productAvailability,
  user,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fetcher = useFetcher<AddToCartActionResponse>();
  const formRef = useRef<HTMLFormElement>(null);
  const { pageViewID } = useEasySize({
    user,
    product,
    productAvailability,
    formRef,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const hasQuantities = Array.from(
      new FormData(event.currentTarget).entries(),
    ).some(([key, value]) => key !== "esid" && parseInt(value as string) > 0);

    if (hasQuantities) return;

    event.preventDefault();
    alert("Please select a quantity for at least one size.");
  };

  const handleBlur = (event: React.FocusEvent<HTMLFormElement>) => {
    const size = event.target.id;
    const max = parseInt(event.target.max || "0");
    const value = parseInt(event.target.value || "0");

    if (value <= max) {
      return;
    }

    alert(
      `Only ${max.toLocaleString()} of size "${size}" are available. We'll adjust the quantity for you.`,
    );

    event.target.value = max.toString();
    event.target.classList.remove("text-red200");
  };

  const handleChange = (event: React.ChangeEvent<HTMLFormElement>) => {
    const hasError = event.target.classList.contains("text-red200");
    const max = parseInt(event.target.max || "0");
    const value = parseInt(event.target.value || "0");

    if (value > max && !hasError) {
      event.target.classList.add("text-red200");
      return;
    }

    if (hasError) {
      event.target.classList.remove("text-red200");
      return;
    }
  };

  useEffect(
    function handleAddToCartSubmit() {
      if (fetcher.state !== "idle" && !fetcher.data) return;
      formRef.current?.reset();
    },
    [fetcher.state, fetcher.data],
  );

  useEffect(
    function handleAddToCartSubmitSuccess() {
      if (!fetcher.data?.ok) return;

      (fetcher.data.data?.gtmEvents ?? []).forEach((event) => {
        (window as WindowWithGTM).dataLayer?.push(event);
      });

      setIsModalOpen(true);
    },
    [fetcher.data],
  );

  const isLoading = ["loading", "submitting"].includes(fetcher.state);

  return (
    <>
      <SuccessModal
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
        actionResponse={fetcher.data}
        selectedVariant={selectedVariant}
        product={product}
      />
      <fetcher.Form
        ref={formRef}
        onSubmit={handleSubmit}
        onBlur={handleBlur}
        onChange={handleChange}
        method="post"
        name="addToCart"
        className="add-to-cart mb-4 flex w-full flex-col gap-3"
      >
        {children}
        <input
          type="hidden"
          name="esid"
          defaultValue={pageViewID}
        />
        <div className="flex flex-col items-center gap-5">
          <button
            type="submit"
            className="add-to-cart__button flex w-4/5 items-center justify-center gap-3 rounded-xmd bg-green200 px-5 py-5 text-[1.5625rem] font-light uppercase leading-none text-white [font-family:'Helvetica_Neue',_Helvetica,_Arial,_sans-serif] hover:bg-green250"
          >
            <svg className="h-[1.75rem] w-[1.75rem] fill-white">
              <use href="/frontend-assets/images/icons.svg#cart" />
            </svg>
            {isLoading ? "Adding..." : "Add to Cart"}
          </button>
          <a
            href="/cart"
            className="text-sm text-blue400 underline visited:text-blue450"
          >
            View Cart &amp; Checkout
          </a>
          <div className="add-to-cart__easy-size hidden" />
        </div>
        <div className="flex flex-col gap-5 py-6">
          {product.information.productMessages.map((message, i) => (
            <div
              key={i}
              dangerouslySetInnerHTML={{ __html: message }}
              className="add-to-cart__message w-1/1 relative mx-3 flex justify-center gap-5 rounded-sm border border-orange300 p-2 pb-2 pl-[36px] pr-2 pt-3 text-center text-sm text-black100 before:absolute before:left-2 before:block before:w-5 before:rounded-full before:bg-orange before:font-serif before:font-bold before:text-white before:content-['i']"
            />
          ))}
        </div>
      </fetcher.Form>
    </>
  );
};

export default ProductAddToCart;
